import type { MakerAgent, MakerAgentUpsert, MarketplaceSettings, OwnerRef, PublishRequest } from '@ralph/contracts'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  archiveMakerAgent,
  createMakerAgent,
  featureListing,
  fetchAdminListings,
  fetchMakerAgent,
  fetchMakerAgents,
  fetchMakerCapabilities,
  fetchMakerCategories,
  fetchMakerVersions,
  fetchMarketplaceSettings,
  fetchNeedsOwner,
  inviteMakerEditor,
  moveMakerAgent,
  publishMakerAgent,
  removeMakerEditor,
  respondToEditorInvite,
  restoreMakerAgent,
  restoreMakerVersion,
  saveMakerAgent,
  saveMarketplaceSettings,
  setListingOfficial,
  startMakerPreview,
  suspendListing,
  unpublishMakerAgent,
  unsuspendListing
} from '@/api/maker.ts'

/**
 * Query hooks for the builder and the studio.
 *
 * Writes invalidate rather than patch, for the same reason the admin studio does it: every
 * save moves the definition version that the NEXT save must send as `If-Match`, and a
 * hand-patched cache guessing that number would manufacture the exact conflict this screen
 * exists to avoid.
 *
 * The catalogue reads (`capabilities`, `categories`, admin `listings`) are deliberately
 * un-retried. Each has a designed degraded state in the UI, and retrying a 404 while the
 * backing route is still being built only delays that state behind three failed requests.
 *
 * A write that changes who can use an agent, or who maintains it — publishing, retiring,
 * restoring, moving — invalidates more than the maker's own reads (PRD-26): the sharing request
 * it opened or closed, the gallery and the agent's page, and the knowledge side, whose "Agents
 * that use it" lists count the people an agent reaches.
 */

export const makerKeys = {
  all: ['maker'] as const,
  agents: ['maker', 'agents'] as const,
  agent: (agentId: string) => ['maker', 'agent', agentId] as const,
  versions: (agentId: string) => ['maker', 'versions', agentId] as const,
  capabilities: ['maker', 'capabilities'] as const,
  categories: ['maker', 'categories'] as const,
  listings: ['admin', 'marketplace', 'listings'] as const,
  needsOwner: ['admin', 'marketplace', 'needs-owner'] as const,
  settings: ['admin', 'marketplace', 'settings'] as const
}

/**
 * Coverage is measured on the saved attachments (GRP-045), so a save or a version restore that
 * changes them has to re-read it, or a base attached a moment ago keeps saying "save to see".
 */
const coverageKey = (agentId: string) => ['knowledge-bases', 'coverage', agentId] as const

/** Everything whose answer moves when an agent's audience, state or owner does. */
const useReachInvalidation = (agentId: string) => {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: makerKeys.agent(agentId) })
    void client.invalidateQueries({ queryKey: makerKeys.agents })
    void client.invalidateQueries({ queryKey: makerKeys.listings })
    void client.invalidateQueries({ queryKey: ['sharing'] })
    void client.invalidateQueries({ queryKey: ['marketplace'] })
    void client.invalidateQueries({ queryKey: ['knowledge-bases'] })
    void client.invalidateQueries({ queryKey: ['groups', 'detail'] })
    // The picker's "than now" is the live reach; after a publish it is the new one.
    void client.invalidateQueries({ queryKey: ['groups', 'reach'] })
  }
}

// ---------------------------------------------------------------------------
// Maker plane
// ---------------------------------------------------------------------------

export const useMakerAgents = (enabled = true) =>
  useQuery({ queryKey: makerKeys.agents, queryFn: fetchMakerAgents, enabled, staleTime: 15_000 })

export const useMakerAgent = (agentId: string | null) =>
  useQuery({
    queryKey: makerKeys.agent(agentId ?? ''),
    queryFn: () => fetchMakerAgent(agentId ?? ''),
    enabled: Boolean(agentId),
    staleTime: 15_000
  })

export const useCreateMakerAgent = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { agentId: string; displayName: string; owner?: OwnerRef }) => createMakerAgent(input),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.agents })
  })
}

export const useSaveMakerAgent = (agentId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ definitionVersion, input }: { definitionVersion: number; input: MakerAgentUpsert }) =>
      saveMakerAgent(agentId, definitionVersion, input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: makerKeys.agent(agentId) })
      void client.invalidateQueries({ queryKey: makerKeys.agents })
      void client.invalidateQueries({ queryKey: makerKeys.versions(agentId) })
      void client.invalidateQueries({ queryKey: coverageKey(agentId) })
    }
  })
}

export const usePublishMakerAgent = (agentId: string) => {
  const invalidate = useReachInvalidation(agentId)
  return useMutation({
    mutationFn: (input: PublishRequest) => publishMakerAgent(agentId, input),
    onSuccess: invalidate
  })
}

/**
 * Bringing a retired agent back chooses who can use it, exactly as publishing does, so it takes
 * the same body and answers with the same outcome (PRD-26 D-50).
 */
export const useRestoreMakerAgent = (agentId: string) => {
  const invalidate = useReachInvalidation(agentId)
  return useMutation({
    mutationFn: (input: PublishRequest) => restoreMakerAgent(agentId, input),
    onSuccess: invalidate
  })
}

/**
 * Unpublish and archive differ only in their verb, so they share one hook. Either closes a
 * sharing request still waiting for review (D-51), which is why the sharing reads refetch too.
 */
export const useListingTransition = (agentId: string) => {
  const invalidate = useReachInvalidation(agentId)
  return useMutation({
    mutationFn: (action: 'unpublish' | 'archive'): Promise<MakerAgent> =>
      action === 'unpublish' ? unpublishMakerAgent(agentId) : archiveMakerAgent(agentId),
    onSuccess: invalidate
  })
}

/** Moves an agent to another owner (D-32). Who can use it never changes; who maintains it does. */
export const useMoveAgent = (agentId: string) => {
  const invalidate = useReachInvalidation(agentId)
  const client = useQueryClient()
  return useMutation({
    mutationFn: (to: OwnerRef) => moveMakerAgent(agentId, to),
    onSuccess: () => {
      invalidate()
      void client.invalidateQueries({ queryKey: makerKeys.needsOwner })
    }
  })
}

/**
 * Opens a preview conversation.
 *
 * A mutation rather than a query even though the pane reads it once: it creates a
 * conversation row, and a query would re-run it on every remount and leave a trail of empty
 * preview conversations behind.
 */
export const useStartPreview = (agentId: string) => useMutation({ mutationFn: () => startMakerPreview(agentId) })

export const useMakerVersions = (agentId: string, enabled: boolean) =>
  useQuery({
    queryKey: makerKeys.versions(agentId),
    queryFn: () => fetchMakerVersions(agentId),
    enabled,
    staleTime: 30_000
  })

export const useRestoreMakerVersion = (agentId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (version: number) => restoreMakerVersion(agentId, version),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: makerKeys.agent(agentId) })
      void client.invalidateQueries({ queryKey: makerKeys.versions(agentId) })
      void client.invalidateQueries({ queryKey: coverageKey(agentId) })
    }
  })
}

export const useInviteEditor = (agentId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (email: string) => inviteMakerEditor(agentId, email),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.agent(agentId) })
  })
}

export const useRemoveEditor = (agentId: string) => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (userId: string) => removeMakerEditor(agentId, userId),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.agent(agentId) })
  })
}

export const useRespondToInvite = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, decision }: { agentId: string; decision: 'accept' | 'decline' }) =>
      respondToEditorInvite(agentId, decision),
    onSuccess: (_result, input) => {
      void client.invalidateQueries({ queryKey: makerKeys.agents })
      void client.invalidateQueries({ queryKey: makerKeys.agent(input.agentId) })
    }
  })
}

export const useMakerCapabilities = (enabled = true) =>
  useQuery({
    queryKey: makerKeys.capabilities,
    queryFn: fetchMakerCapabilities,
    enabled,
    retry: false,
    staleTime: 60_000
  })

export const useMakerCategories = (enabled = true) =>
  useQuery({
    queryKey: makerKeys.categories,
    queryFn: fetchMakerCategories,
    enabled,
    retry: false,
    staleTime: 300_000
  })

// ---------------------------------------------------------------------------
// Administrator plane
// ---------------------------------------------------------------------------

export const useAdminListings = (enabled: boolean) =>
  useQuery({ queryKey: makerKeys.listings, queryFn: fetchAdminListings, enabled, retry: false, staleTime: 15_000 })

export const useSuspendListing = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({
      agentId,
      ...input
    }: {
      agentId: string
      reasonPublic: string
      reasonFull: string
      replacementAgentId: string | null
    }) => suspendListing(agentId, input),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.listings })
  })
}

export const useUnsuspendListing = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (agentId: string) => unsuspendListing(agentId),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.listings })
  })
}

export const useFeatureListing = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, rank }: { agentId: string; rank: number | null }) => featureListing(agentId, rank),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.listings })
  })
}

export const useSetListingOfficial = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, value }: { agentId: string; value: boolean }) => setListingOfficial(agentId, value),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.listings })
  })
}

export const useNeedsOwner = (enabled: boolean) =>
  useQuery({ queryKey: makerKeys.needsOwner, queryFn: fetchNeedsOwner, enabled, retry: false, staleTime: 30_000 })

/**
 * An administrator giving an agent to someone (AGM-025). The admin transfer route is gone: this
 * is the move (D-32), which administrators may point anywhere — a person or a group.
 */
export const useTransferListing = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: ({ agentId, ownerId, to }: { agentId: string; ownerId?: string; to?: OwnerRef }) =>
      moveMakerAgent(agentId, to ?? { kind: 'user', id: ownerId ?? '' }),
    onSuccess: (_agent, input) => {
      void client.invalidateQueries({ queryKey: makerKeys.needsOwner })
      void client.invalidateQueries({ queryKey: makerKeys.listings })
      void client.invalidateQueries({ queryKey: makerKeys.agent(input.agentId) })
      void client.invalidateQueries({ queryKey: ['marketplace'] })
    }
  })
}

export const useMarketplaceSettings = (enabled: boolean) =>
  useQuery({ queryKey: makerKeys.settings, queryFn: fetchMarketplaceSettings, enabled, retry: false })

export const useSaveMarketplaceSettings = () => {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (settings: MarketplaceSettings) => saveMarketplaceSettings(settings),
    onSuccess: () => client.invalidateQueries({ queryKey: makerKeys.settings })
  })
}
